import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../utils/api';
import { accountClosingText } from '../common/leavingSchool';

// Where each of the student's tests stands, from the server (GET /api/my-tests/, custom_auth/
// my_tests.py): open, in_progress, done or closed, the due date that really applies to them
// (an extension first) and why a closed test is closed. The test picker, the chat button, the
// resume check and the Results page all read this, so they never disagree with each other.
//
// useMyTests() -> { tests, loading, error, reload }
//   tests: [{ type, status, due, reason, answered, total, resume_until, due_at, updated_at }] or null

// The names students see (the test picker's plain names)
export const TEST_NAMES = {
  riasec: { en: 'Career interests', fil: 'Career interests' },
  dass42: { en: 'Mood and stress', fil: 'Mood and stress' },
  ryff: { en: 'Well-being', fil: 'Well-being' },
  sdq: { en: 'Strengths and difficulties', fil: 'Strengths and difficulties' },
};
export const testName = (type, lang = 'en') => (TEST_NAMES[type] || {})[lang === 'fil' ? 'fil' : 'en'] || type;

// Several screens ask at once when the chat opens: they share one request, and a reply this
// fresh is reused instead of asking again
const FRESH_MS = 1500;
let inflight = null;
let last = null;

const signedIn = () => {
  try {
    return Boolean(window.localStorage.getItem('authToken'));
  } catch {
    return false;
  }
};

/** The student's tests (a fresh copy unless one just arrived); null when nobody is signed in. */
export const fetchMyTests = async ({ fresh = false } = {}) => {
  if (!signedIn()) return null;
  if (!fresh && last && Date.now() - last.at < FRESH_MS) return last.tests;
  if (!inflight) {
    inflight = api
      .get('/api/my-tests/', { skipErrorToast: true })
      .then((response) => {
        const tests = Array.isArray(response?.data?.tests) ? response.data.tests : [];
        last = { at: Date.now(), tests };
        return tests;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
};

/** Tests only: forget the shared reply. */
export const resetMyTestsCache = () => {
  inflight = null;
  last = null;
};

/** { riasec: {...}, dass42: {...} } from the list (empty when there is none). */
export const testsByType = (tests) =>
  Object.fromEntries((Array.isArray(tests) ? tests : []).map((test) => [test.type, test]));

/** 'YYYY-MM-DD' as a local date (never shifted a day by the time zone), or null. */
export const dueDate = (due) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(due || ''));
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};

/** "Oct 15", with the year only when it isn't this year ("Oct 15, 2027"). */
export const shortDay = (due) => {
  const date = dueDate(due);
  if (!date) return '';
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
};

/** "Nov 5, 2026" */
export const longDay = (due) => {
  const date = dueDate(due);
  return date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
};

/**
 * Why a test can't be taken, in plain words: "Closed on Oct 15. Ask the guidance office if you
 * still want to take it."
 */
export const closedText = (test, lang = 'en') => {
  const fil = lang === 'fil';
  if (!test) return '';
  // C4: the guidance office is closing this account (my_tests closed_reason)
  if (test.closed_reason === 'account_closing' || test.reason === 'account_closing') {
    return accountClosingText(test.closes_on || null, lang);
  }
  if (test.reason === 'no_starts_left') {
    return fil
      ? 'Wala ka nang natitirang subok. Magtanong sa guidance office kung gusto mo pang kumuha ulit.'
      : 'No tries left. Ask the guidance office if you want to take it again.';
  }
  if (test.reason === 'not_assigned') {
    return fil
      ? 'Wala na ito sa listahan mo. Magtanong sa guidance office kung sa tingin mo ay mali ito.'
      : "This isn't on your list any more. Ask the guidance office if you think that's a mistake.";
  }
  const day = shortDay(test.due);
  if (fil) {
    return day
      ? `Sarado na noong ${day}. Magtanong sa guidance office kung gusto mo pa itong kunin.`
      : 'Sarado na ito. Magtanong sa guidance office kung gusto mo pa itong kunin.';
  }
  return day
    ? `Closed on ${day}. Ask the guidance office if you still want to take it.`
    : 'This test is closed. Ask the guidance office if you still want to take it.';
};

/** Every assigned test is finished (and there is at least one). */
export const allTestsDone = (tests) =>
  Array.isArray(tests) && tests.filter((test) => test.reason !== 'not_assigned').length > 0 &&
  tests.filter((test) => test.reason !== 'not_assigned').every((test) => test.status === 'done');

/** A started test with every answer saved, waiting only to be sent (F7). */
export const readyToFinish = (test) =>
  Boolean(test) && test.status === 'in_progress' && Number(test.total) > 0 && Number(test.answered) >= Number(test.total);

export const useMyTests = ({ skip = false } = {}) => {
  const [tests, setTests] = useState(() => (last && Date.now() - last.at < FRESH_MS ? last.tests : null));
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(null);
  const alive = useRef(true);
  const run = useRef(0);

  const load = useCallback(async (fresh) => {
    run.current += 1;
    const id = run.current;
    setLoading(true);
    setError(null);
    try {
      const next = await fetchMyTests({ fresh });
      if (alive.current && id === run.current) setTests(next);
      return next;
    } catch (err) {
      if (alive.current && id === run.current) setError(err);
      return null;
    } finally {
      if (alive.current && id === run.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    if (!skip) load(false);
    return () => {
      alive.current = false;
    };
  }, [skip, load]);

  const reload = useCallback(() => load(true), [load]);
  return { tests, loading, error, reload };
};

export default useMyTests;
